import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import validator from "validator";
import { useSnackbar } from "notistack";
import { Box, TextField, Button } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants.js";

// Rounded outlined inputs with a green focus/hover border (matches the app design system).
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& label.Mui-focused": {
    color: "hsl(153, 68%, 25%)",
  },
};

const Invitation = ({ onClose }) => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const companyId = useSelector((state) => state.current_recruiter.company.id);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});

  const [recruiterData, setRecruiterData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    company_id: companyId,
  });

  const validate = (fieldValues = recruiterData) => {
    let temp = { ...errors };
    let required = <FormattedMessage id="settings.invitations.required" />;
    let notAnEmail = <FormattedMessage id="settings.invitations.not_email" />;

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
      if (fieldValues.email && !validator.isEmail(fieldValues.email))
        temp.email = notAnEmail;
    }

    if ("first_name" in fieldValues) {
      temp.first_name = fieldValues.first_name ? "" : required;
    }

    if ("last_name" in fieldValues) {
      temp.last_name = fieldValues.last_name ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecruiterData({
      ...recruiterData,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
    case "ALREADY_TEAMMATE":
      return intl.formatMessage({ id: "settings.invitations.already_teammate" });
    case "ALREADY_INVITED":
      return intl.formatMessage({ id: "settings.invitations.already_invited" });
    case "EXISTS_DIFFERENT_COMPANY":
      return intl.formatMessage({ id: "settings.invitations.exists_different_company" });
    default:
      return intl.formatMessage({ id: "settings.invitations.inviting_error" });
    }
  };

  const handleSubmit = () => {
    if (validate()) {
      sh.post(`/${i18nLocale}/recruiters/invitation`, {
        authenticity_token: authenticityToken,
        recruiter: recruiterData,
      })
        .then(() => {
          replaceCurrentRecruiter(dispatch, authenticityToken);
          enqueueSnackbar(intl.formatMessage({ id: "settings.invitations.invitation_sent" }), {
            variant: "success",
          });
          onClose();
        })
        .catch((error) => {
          if (error.response && error.response.status === 409) {
            const errorCode = error.response.data.code;
            const errorMessage = getErrorMessage(errorCode);
            enqueueSnackbar(errorMessage, { variant: "error" });
          } else {
            enqueueSnackbar(intl.formatMessage({ id: "settings.invitations.inviting_error" }), {
              variant: "error",
            });
          }
        });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(2, 51, 49, 0.18)",
        p: { xs: "24px", md: "40px 48px" },
        width: { xs: "calc(100% - 32px)", md: "600px" },
        maxWidth: "600px",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <Box
        component="h2"
        sx={{
          textAlign: "center",
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          fontSize: "1.25rem",
          color: "hsl(178, 93%, 10%)",
          letterSpacing: "-0.01em",
          m: 0,
          mb: "28px",
        }}
      >
        <FormattedMessage id="settings.invitations.title" />
      </Box>
      <TextField
        value={recruiterData.email}
        onChange={handleInputChange}
        label={<FormattedMessage id="settings.invitations.recruiter_email" />}
        variant="outlined"
        fullWidth
        name="email"
        sx={{ ...inputSx, mb: "20px" }}
        error={!!errors.email}
        helperText={
          errors.email ? (
            <FormattedMessage id="settings.invitations.email_error" />
          ) : (
            ""
          )
        }
      />
      <TextField
        value={recruiterData.first_name}
        onChange={handleInputChange}
        label={<FormattedMessage id="settings.invitations.first_name" />}
        variant="outlined"
        fullWidth
        name="first_name"
        sx={{ ...inputSx, mb: "20px" }}
        error={!!errors.first_name}
        helperText={errors.first_name}
      />
      <TextField
        value={recruiterData.last_name}
        onChange={handleInputChange}
        label={<FormattedMessage id="settings.invitations.last_name" />}
        variant="outlined"
        fullWidth
        name="last_name"
        sx={{ ...inputSx, mb: "28px" }}
        error={!!errors.last_name}
        helperText={errors.last_name}
      />
      <Button
        onClick={handleSubmit}
        fullWidth
        variant="rounded"
        sx={{
          textTransform: "none",
          fontFamily: "Segoe UI Semibold, sans-serif",
          fontSize: "0.95rem",
          py: 1,
          backgroundColor: "hsl(153 68% 25%)",
          color: "white",
          boxShadow: "none",
          transition: "all 180ms ease",
          "&:hover": {
            backgroundColor: "hsl(153 68% 22%)",
            boxShadow: "none",
          },
        }}
      >
        <FormattedMessage id="settings.invitations.send_invitation" />
      </Button>
    </Box>
  );
};

export default Invitation;
